/**
 * Google Ads Tag Loader Utility
 *
 * This module handles loading and initialization of Google Ads (Google Tag)
 * with proper environment validation and consent mode integration.
 *
 * IMPORTANT: This must load BEFORE GTM to ensure conversion tags work properly.
 */

const PRODUCTION_ADS_ID = 'AW-17612391031';

class GoogleAdsLoader {
  constructor() {
    this.adsId = process.env.REACT_APP_GOOGLE_ADS_ID;
    this.environment = process.env.REACT_APP_CURRENT_ENV || 'development';
    this.isLoaded = false;
    this.isValidated = false;
    // SSR-safe localStorage access
    this.debugMode =
      typeof window !== 'undefined' &&
      localStorage.getItem('gtm_debug') === 'true';
  }

  /**
   * Validate Google Ads ID and environment
   */
  validateEnvironment() {
    const validations = {
      isValidAdsId:
        this.adsId &&
        this.adsId.startsWith('AW-') &&
        this.adsId !== 'AW-XXXXXXXXXX',
      hasRequiredConfig: Boolean(this.adsId && this.environment)
    };

    // Environment-specific validation
    if (this.environment === 'production') {
      // In production, ensure we're using the production Ads ID
      validations.environmentMatch = this.adsId === PRODUCTION_ADS_ID;
      if (!validations.environmentMatch && this.debugMode) {
        console.warn(
          `**PROD** environment should use production Google Ads ID (${PRODUCTION_ADS_ID}). Current: ${this.adsId}`
        );
      }
    } else if (
      this.environment === 'development' ||
      this.environment === 'pre-production'
    ) {
      // For dev/staging, allow any valid AW- ID (including test IDs)
      validations.environmentMatch = validations.isValidAdsId;
      if (this.debugMode) {
        console.log(
          `**${this.environment.toUpperCase()}** environment using Google Ads ID: ${this.adsId}`
        );
      }
    } else {
      console.error('Unknown environment:', this.environment);
      validations.environmentMatch = false;
    }

    // Debug logging
    if (this.debugMode) {
      console.log('🔍 Google Ads Environment Validation:', {
        environment: this.environment,
        adsId: this.adsId,
        validations
      });
    }

    this.isValidated = Object.values(validations).every(Boolean);
    return this.isValidated;
  }

  /**
   * Load Google Ads gtag.js script dynamically
   */
  loadGoogleAdsScript() {
    return new Promise((resolve, reject) => {
      if (this.isLoaded) {
        if (this.debugMode) {
          console.log('✅ Google Ads script already loaded');
        }
        resolve(true);
        return;
      }

      if (!this.validateEnvironment()) {
        const error = 'Google Ads environment validation failed';
        console.error('❌', error);
        reject(new Error(error));
        return;
      }

      try {
        // Initialize dataLayer if not exists (should already exist from consent mode)
        window.dataLayer = window.dataLayer || [];

        // Initialize gtag function if not exists
        if (!window.gtag) {
          window.gtag = function () {
            window.dataLayer.push(arguments);
          };
        }

        // Check if script already exists in DOM
        const existingScript = document.querySelector(
          `script[src*="gtag/js?id=${this.adsId}"]`
        );
        if (existingScript) {
          this.isLoaded = true;
          if (this.debugMode) {
            console.log('✅ Google Ads script already in DOM');
          }
          resolve(true);
          return;
        }

        // Create and inject gtag.js script
        const script = document.createElement('script');
        script.async = true;
        script.src = `https://www.googletagmanager.com/gtag/js?id=${this.adsId}`;

        script.onload = () => {
          this.isLoaded = true;

          // Configure the Google Ads tag
          this.configureGoogleAds();

          if (this.debugMode) {
            console.log(
              '✅ Google Ads script loaded successfully:',
              this.adsId
            );
          }
          resolve(true);
        };

        script.onerror = () => {
          const error = 'Failed to load Google Ads script';
          console.error('❌', error);
          reject(new Error(error));
        };

        // Inject script into head
        document.head.appendChild(script);
      } catch (error) {
        console.error('❌ Error loading Google Ads:', error);
        reject(error);
      }
    });
  }

  /**
   * Configure Google Ads tag with consent mode integration
   * @private
   */
  configureGoogleAds() {
    if (!window.gtag) {
      console.error('❌ gtag function not available');
      return;
    }

    // Set timestamp
    window.gtag('js', new Date());

    // Configure Google Ads tag with consent-aware settings
    window.gtag('config', this.adsId, {
      // Disable ad personalization by default (will be enabled via consent mode)
      allow_ad_personalization_signals: false,

      // Enable enhanced conversions for better tracking accuracy
      allow_enhanced_conversions: true,

      // Send page view automatically
      send_page_view: true
    });

    if (this.debugMode) {
      console.log('✅ Google Ads tag configured:', {
        adsId: this.adsId,
        config: {
          allow_ad_personalization_signals: false,
          allow_enhanced_conversions: true,
          send_page_view: true
        }
      });
    }
  }

  /**
   * Check if Google Ads is ready to use
   * @returns {boolean} True if loaded and validated
   */
  isReady() {
    return this.isLoaded && this.isValidated && window.gtag;
  }

  /**
   * Get current configuration
   * @returns {Object} Current configuration state
   */
  getConfig() {
    return {
      adsId: this.adsId,
      environment: this.environment,
      isLoaded: this.isLoaded,
      isValidated: this.isValidated,
      debugMode: this.debugMode
    };
  }

  /**
   * Enable/disable debug mode
   * @param {boolean} enabled - Debug mode state
   */
  setDebugMode(enabled) {
    this.debugMode = enabled;
    if (enabled) {
      localStorage.setItem('gtm_debug', 'true');
      console.log('🔍 Google Ads Debug mode enabled');
    } else {
      localStorage.removeItem('gtm_debug');
      console.log('🔍 Google Ads Debug mode disabled');
    }
  }

  /**
   * Track a conversion event
   * @param {string} conversionLabel - Conversion label from Google Ads
   * @param {{value?: number, currency?: string, transaction_id?: string}} conversionData - Additional conversion data
   * @returns {void}
   */
  trackConversion(conversionLabel, conversionData = {}) {
    if (!this.isReady()) {
      if (this.debugMode) {
        console.warn('⚠️ Google Ads not ready for conversion tracking');
      }
      return;
    }

    const { value, currency = 'INR', transaction_id } = conversionData;

    window.gtag('event', 'conversion', {
      send_to: `${this.adsId}/${conversionLabel}`,
      value: value,
      currency: currency,
      transaction_id: transaction_id
    });

    if (this.debugMode) {
      console.log('📊 Google Ads Conversion tracked:', {
        conversion_label: conversionLabel,
        data: conversionData
      });
    }
  }

  /**
   * Track a remarketing event
   * @param {string} eventName - Event name
   * @param {Object} eventData - Event parameters (value, currency, items, etc.)
   * @returns {void}
   */
  trackRemarketingEvent(eventName, eventData = {}) {
    if (!this.isReady()) {
      if (this.debugMode) {
        console.warn('⚠️ Google Ads not ready for remarketing tracking');
      }
      return;
    }

    window.gtag('event', eventName, {
      send_to: this.adsId,
      ...eventData
    });

    if (this.debugMode) {
      console.log('📊 Google Ads Remarketing event tracked:', {
        event: eventName,
        data: eventData
      });
    }
  }

  /**
   * Force reload Google Ads (for testing purposes)
   * @returns {Promise<boolean>}
   */
  async reload() {
    this.isLoaded = false;
    this.isValidated = false;

    // Remove existing scripts
    const existingScripts = document.querySelectorAll(
      `script[src*="gtag/js?id=${this.adsId}"]`
    );
    existingScripts.forEach((script) => script.remove());

    // Reload
    return this.loadGoogleAdsScript();
  }
}

// Create singleton instance
const googleAdsLoader = new GoogleAdsLoader();

export default googleAdsLoader;

// Export convenience methods
export const {
  loadGoogleAdsScript,
  validateEnvironment,
  isReady,
  getConfig,
  setDebugMode,
  trackConversion,
  trackRemarketingEvent,
  reload
} = googleAdsLoader;
